import React, { useState, useEffect } from 'react';
import {
    getFirestore,
    doc,
    setDoc,
    getDoc
} from 'firebase/firestore';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile
} from 'firebase/auth';
import { auth } from '../firebase';
import {validateClassCode, addStudentToRoster, validateExplorerCode} from '../utils/instructorUtils';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Alert,
    CircularProgress
} from '@mui/material';
import { useTranslation } from "react-i18next";

const UnifiedAuth = ({ mode = 'signup', onClose }) => {
    const [authType, setAuthType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [registrationDisabled, setRegistrationDisabled] = useState(true);
    const [disabledMessage, setDisabledMessage] = useState('');
    const {t} = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        displayName: '',
        classCode: '',
    });
    const passwordsMatch = mode !== 'signup' || formData.password === formData.confirmPassword;

    // Check registration status on component mount
    useEffect(() => {
        const checkRegistrationStatus = async () => {
            try {
                const db = getFirestore();
                const settingsDoc = await getDoc(doc(db, 'settings', 'registration'));

                if (settingsDoc.exists()) {
                    const settings = settingsDoc.data();
                    setRegistrationDisabled(settings.disabled);
                    setDisabledMessage(settings.message || t('auth.disabled'));


                } else {
                    setRegistrationDisabled(false);
                }
            } catch (error) {
                console.error('Error checking registration status:', error);
                setError(t('auth.unableCheck'));
            } finally {
                setLoading(false);
            }
        };

        if (mode === 'signup') {
            checkRegistrationStatus();
        } else {
            setLoading(false);
        }
    }, [t, mode]);

    // Generate display name whenever first or last name changes
    useEffect(() => {
        if (formData.firstName && formData.lastName) {
            const displayName = `${formData.firstName} ${formData.lastName}`;
            setFormData(prev => ({...prev, displayName}));
        }
    }, [formData.firstName, formData.lastName]);

    const createUserWithRole = async (email, password, role, userData) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        if (!userCredential?.user) {
            throw new Error(t('auth.failedCreate'));
        }

        await updateProfile(userCredential.user, {
            displayName: userData.displayName
        });

        const db = getFirestore();
        await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: email.toLowerCase(),
            role: role,
            ...userData,
            createdAt: new Date().toISOString()
        });

        return userCredential;
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'classCode' ? value.toUpperCase() : value
        }));
        if (name === 'classCode') {
            setError('');
        }
    };

    const handleAuthTypeChange = (e) => {
        const newType = e.target.value;
        // If registrations are disabled, only allow classroom type
        if (registrationDisabled && mode === 'signup' && newType !== 'classroom') {
            setError(t('auth.disabled'));
            return;
        }
        setAuthType(newType);
        setError(''); // Clear any existing errors
    };

    const getAuthErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/user-not-found':
                return t('auth.noAccount');
            case 'auth/wrong-password':
                return t('auth.badPassword');
            case 'auth/invalid-email':
                return t('auth.badEmail');
            case 'auth/too-many-requests':
                return t('auth.tooManyAttempts');
            default:
                return t('auth.errorGeneric');
        }
    };

    const handleLogin = async () => {
        try {
            let loginEmail = formData.email;

            if (authType === 'classroom') {
                if (!formData.classCode) {
                    throw new Error(t('auth.enterClassCode'));
                }
                const isValidCode = await validateClassCode(formData.classCode);
                if (!isValidCode) {
                    throw new Error(t('auth.invalidCode'));
                }
                loginEmail = `${formData.firstName.toLowerCase()}.${formData.lastName.toLowerCase()}.${formData.classCode.toLowerCase()}@classroom.meld`;
            } else if (authType === 'explorer') {
                if (!formData.explorerCode) {
                    throw new Error(t('auth.enterExplorerCode'));
                }
                const isValidCode = await validateExplorerCode(formData.explorerCode);
                if (!isValidCode) {
                    throw new Error(t('auth.invalidExplorerCode'));
                }
            }

            await signInWithEmailAndPassword(auth, loginEmail, formData.password);
            await new Promise(resolve => setTimeout(resolve, 1000));
            onClose();
        } catch (error) {
            if (error.message.includes('Code')) {
                setError(error.message);
            } else {
                setError(getAuthErrorMessage(error.code));
            }
        }
    };

    const handleSignup = async () => {
        // registration disabled prevents instructor and student email signups
        if (registrationDisabled && authType !== 'classroom' && authType !== 'explorer') {
            setError(t('auth.disabled'));
            return;
        }

        try {
            if (authType === 'classroom') {
                const isValidCode = await validateClassCode(formData.classCode);
                if (!isValidCode) {
                    setError(t('auth.invalidCode'));
                    return;
                }
            } else if (authType === 'explorer') {
                const isValidCode = await validateExplorerCode(formData.explorerCode);
                if (!isValidCode) {
                    setError(t('auth.invalidExplorerCode'));
                    return;
                }
            }
        } catch (error) {
            console.error('Code validation error:', error);
            setError(t('auth.badValid'));
            return;
        }


        let email = formData.email;
        if (authType === 'classroom') {
            email = `${formData.firstName.toLowerCase()}.${formData.lastName.toLowerCase()}.${formData.classCode.toLowerCase()}@classroom.meld`;
        }

        try {
            const userData = {
                displayName: formData.displayName,
                firstName: formData.firstName,
                lastName: formData.lastName,
                ...(authType === 'classroom' && {classCode: formData.classCode}),
                ...(authType === 'explorer' && {explorerCode: formData.explorerCode})
            };

            const role = {
                'classroom': 'student',
                'permanent': 'student',
                'instructor': 'instructor',
                'explorer': 'explorer'
            }[authType];

            const userCredential = await createUserWithRole(email, formData.password, role, userData);

            if (!userCredential?.user) {
                throw new Error(t('auth.failedCreate'));
            }

            await updateProfile(userCredential.user, {
                displayName: formData.displayName
            });


            if (authType === 'classroom') {
                await addStudentToRoster(formData.classCode, email);
            }

            // Wait for Firestore write to propagate
            await new Promise(resolve => setTimeout(resolve, 2000));
            window.location.reload(); // Force reload to ensure fresh auth state
            onClose();

        } catch (error) {
            console.error('Auth error:', error);
            let errorMessage = t('auth.regFailed') + ': ';

            if (error.code) {
                switch (error.code) {
                    case 'auth/email-already-in-use':
                        errorMessage += t('auth.failedEmail');
                        break;
                    case 'auth/invalid-email':
                        errorMessage += t('auth.badEmail');
                        break;
                    case 'auth/weak-password':
                        errorMessage += t('auth.weakPassword');
                        break;
                    default:
                        errorMessage += error.message;
                }
            } else {
                errorMessage += error.message;
            }
            setError(errorMessage);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!passwordsMatch) {
            setError(t('auth.passwordMismatch'));
            return;
        }

        try {
            if (mode === 'login') {
                await handleLogin();
            } else {
                await handleSignup();
            }
        } finally {
            setLoading(false);
        }
    };

    const AuthOptions = ({disabled, onSelect}) => (
        <Stack spacing={2}>
            <Button
                fullWidth
                variant="outlined"
                onClick={() => onSelect('permanent')}
                disabled={disabled}
                sx={{py: 2}}
                title={disabled ? t('auth.disabled') : t('auth.studentEmailDesc')}
            >
                <Stack>
                    <Typography variant="subtitle1">{t('auth.studentEmail')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('auth.studentEmailDesc')}
                    </Typography>
                </Stack>
            </Button>

            <Button
                fullWidth
                variant="outlined"
                onClick={() => onSelect('classroom')}
                sx={{py: 2}}
                title={t('auth.studentClassDesc')}
            >
                <Stack>
                    <Typography variant="subtitle1">{t('auth.studentClass')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('auth.studentClassDesc')}
                    </Typography>
                </Stack>
            </Button>

            <Button
                fullWidth
                variant="outlined"
                onClick={() => onSelect('instructor')}
                disabled={disabled}
                sx={{py: 2}}
                title={disabled ? t('auth.disabled') : t('auth.instructorDesc')}
            >
                <Stack>
                    <Typography variant="subtitle1">{t('auth.instructor')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('auth.instructorDesc')}
                    </Typography>
                </Stack>
            </Button>

            <Button
                fullWidth
                variant="outlined"
                onClick={() => onSelect('explorer')}
                sx={{py: 2}}
                title={t('auth.explorerDesc')}
            >
                <Stack>
                    <Typography variant="subtitle1">{t('auth.explorer')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('auth.explorerDesc')}
                    </Typography>
                </Stack>
            </Button>
        </Stack>
    );

    const getFormFields = () => {
        if (authType === 'classroom') {
            return (
                <Stack spacing={1}>
                    <TextField
                        fullWidth
                        label={t('auth.firstNameLabel')}
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label={t('auth.lastNameLabel')}
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label={t('auth.passwordLabel')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    {mode === 'signup' && (
                        <TextField
                            fullWidth
                            label={t('auth.confirmPasswordLabel')}
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            size="small"
                            error={formData.password !== formData.confirmPassword}
                            helperText={
                                formData.password !== formData.confirmPassword ?
                                    t('auth.passwordMismatch') : ''
                            }
                        />
                    )}
                    <TextField
                        fullWidth
                        label={t('auth.classCodeLabel')}
                        name="classCode"
                        value={formData.classCode}
                        onChange={handleInputChange}
                        required
                        size="small"
                        error={!!error && error.includes('code')}
                        helperText={error && error.includes('code') ? error : ''}
                    />
                </Stack>
            );
        } else if (authType === 'explorer') {
            return (
                <Stack spacing={1}>
                    <TextField
                        fullWidth
                        label={t('auth.emailLabel')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label={t('auth.passwordLabel')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    {mode === 'signup' && (
                        <TextField
                            fullWidth
                            label={t('auth.confirmPasswordLabel')}
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            size="small"
                            error={formData.password !== formData.confirmPassword}
                            helperText={
                                formData.password !== formData.confirmPassword ?
                                    t('auth.passwordMismatch') : ''
                            }
                        />
                    )}
                    <TextField
                        fullWidth
                        label={t('auth.explorerCodeLabel')}
                        name="explorerCode"
                        value={formData.explorerCode}
                        onChange={handleInputChange}
                        required
                        size="small"
                        error={!!error && error.includes('explorer')}
                        helperText={error && error.includes('explorer') ? error : ''}
                    />
                </Stack>
            );
        } else {
            // Permanent student or instructor
            return (
                <Stack spacing={1}>
                    {mode === 'signup' && (
                        <>
                            <TextField
                                fullWidth
                                label={t('auth.firstNameLabel')}
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                size="small"
                            />
                            <TextField
                                fullWidth
                                label={t('auth.lastNameLabel')}
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                size="small"
                            />
                        </>
                    )}
                    <TextField
                        fullWidth
                        label={t('auth.emailLabel')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label={t('auth.passwordLabel')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        size="small"
                    />
                    {mode === 'signup' && (
                        <TextField
                            fullWidth
                            label={t('auth.confirmPasswordLabel')}
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            size="small"
                            error={formData.password !== formData.confirmPassword}
                            helperText={
                                formData.password !== formData.confirmPassword ?
                                    t('auth.passwordMismatch') : ''
                            }
                        />
                    )}
                </Stack>
            );
        }
    };

    return (
        <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto' }}>
            <CardContent>
                <Typography variant="h5" component="h2" gutterBottom>
                    {mode === 'signup' ? t('auth.signupLabel') : t('auth.loginLabel')}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {!authType ? (
                            <AuthOptions
                                disabled={mode === 'signup' && registrationDisabled}
                                onSelect={setAuthType}
                            />
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                                {getFormFields()}
                                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                    <Button variant="outlined" onClick={() => setAuthType(null)}>
                                        {t('common.back')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        disabled={loading || !passwordsMatch}
                                    >
                                        {loading ? t('common.loading') : mode === 'signup' ? t('auth.signupLabel') : t('auth.loginLabel')}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default UnifiedAuth;